/**
 * Yönetim panelinin API istemcisi (M8).
 *
 * Sorumluluğu bilerek dardır ve BEŞ şey yapar:
 *   1) adres + sorgu dizesi birleştirme,
 *   2) Authorization başlığı (bu sekmenin aktif personel JWT'si),
 *   3) sunucunun §10.3 hata gövdesini tipli bir istisnaya çevirme,
 *   4) 401'de oturumun düştüğünü kabuğa DUYURMA (olay ile; bu dosya React
 *      bilmez, bu yüzden geri çağırma yerine pencere olayı kullanılır),
 *   5) KK-608: dışa aktarımda sunucunun kırpma uyarısını OKUMA — sessiz
 *      kırpma yok, kullanıcıya gösterilir.
 *
 * PARA KURALI (ADR-03): bu dosyada Number()/parseFloat()/unary + YOKTUR.
 * Sunucudan gelen para alanları string'dir ve string kalır; ekranda <Tutar>,
 * hesapta @kantin/shared'daki para()/paraTopla() kullanılır.
 */
import { HataKodu } from "@kantin/shared";
import { aktifOturum, oturumDurumu } from "./oturum.js";

const TABAN = "/api/v1";

/** Kabuğun dinlediği olay: 401 gelince oturum ekranı geri gelir. */
export const OTURUM_DUSTU = "kantin:oturum-dustu";

export function tokenOku(): string | null {
  return aktifOturum(oturumDurumu.getState())?.token ?? null;
}

/** Giriş isteği ve önceki oturumun yanıtı mevcut oturumu kapatamaz. */
export function oturumDusmesiniBildir(istekTokeni: string | null): void {
  if (istekTokeni !== null && tokenOku() === istekTokeni) {
    window.dispatchEvent(new CustomEvent(OTURUM_DUSTU, { detail: { token: istekTokeni } }));
  }
}

/** Sunucunun §10.3 hata gövdesi. */
export interface HataYaniti {
  hata?: string;
  mesaj?: string;
  detay?: Record<string, string>;
}

export class ApiHatasi extends Error {
  readonly kod: string;
  readonly http: number;
  readonly detay: Record<string, string> | undefined;

  constructor(
    http: number,
    kod: string,
    mesaj: string,
    detay?: Record<string, string>,
  ) {
    super(mesaj);
    this.name = "ApiHatasi";
    this.http = http;
    this.kod = kod;
    this.detay = detay;
  }
}

/**
 * KK-1508: sunucu bazı uçlarda 403 yerine bilerek 404 döner ("orada bir şey
 * var ama göremiyorsun" bilgisi sızmasın diye). Bu yüzden yetkisizlik yalnızca
 * HTTP koduna bakarak anlaşılamaz; kod da kontrol edilir.
 */
export function yetkisizMi(e: unknown): boolean {
  return (
    e instanceof ApiHatasi &&
    (e.http === 403 || e.kod === HataKodu.YETKISIZ || e.kod === "YETKISIZ")
  );
}

export type SorguParam = Record<
  string,
  string | number | boolean | null | undefined
>;

export function adres(yol: string, sorgu?: SorguParam): string {
  if (!sorgu) return `${TABAN}${yol}`;
  const p = new URLSearchParams();
  for (const [anahtar, deger] of Object.entries(sorgu)) {
    if (deger === undefined || deger === null || deger === "") continue;
    p.append(anahtar, String(deger));
  }
  const metin = p.toString();
  return metin ? `${TABAN}${yol}?${metin}` : `${TABAN}${yol}`;
}

function basliklar(jsonMu: boolean, token: string | null): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json" };
  if (jsonMu) h["Content-Type"] = "application/json";
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function hatayiCoz(yanit: Response, istekTokeni: string | null = null): Promise<never> {
  let govde: HataYaniti = {};
  try {
    govde = (await yanit.json()) as HataYaniti;
  } catch {
    // Gövdesi olmayan hata (proxy, ağ kesintisi): aşağıdaki varsayılan mesaj.
  }
  if (yanit.status === 401) {
    oturumDusmesiniBildir(istekTokeni);
  }
  throw new ApiHatasi(
    yanit.status,
    govde.hata ?? HataKodu.SUNUCU_HATASI,
    govde.mesaj ??
      (yanit.status === 401
        ? "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın."
        : `İstek başarısız (HTTP ${yanit.status}).`),
    govde.detay,
  );
}

async function govdeCoz<T>(yanit: Response): Promise<T> {
  if (yanit.status === 204) return undefined as T;
  const metin = await yanit.text();
  if (!metin) return undefined as T;
  return JSON.parse(metin) as T;
}

export async function apiOku<T>(yol: string, sorgu?: SorguParam): Promise<T> {
  const token = tokenOku();
  const yanit = await fetch(adres(yol, sorgu), {
    method: "GET",
    headers: basliklar(false, token),
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  return govdeCoz<T>(yanit);
}

export async function apiYaz<T>(
  yontem: "POST" | "PATCH" | "PUT" | "DELETE",
  yol: string,
  govde?: unknown,
): Promise<T> {
  const token = tokenOku();
  // Content-Type YALNIZCA gövde varken gönderilir. Aksi halde Fastify
  // "Body cannot be empty when content-type is set to 'application/json'"
  // deyip 400 döner ve gövdesiz uçların HEPSİ kırılır: onaylama, mutabakat
  // çözme, token yenileme, anomali işaretleme. (kasa/veli istemcileri de
  // aynı deseni kullanır.)
  const yanit = await fetch(adres(yol), {
    method: yontem,
    headers: basliklar(govde !== undefined, token),
    ...(govde === undefined ? {} : { body: JSON.stringify(govde) }),
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  return govdeCoz<T>(yanit);
}

/**
 * Giriş — POST /kimlik/personel/giris. Token'ı BU FONKSİYON YAZMAZ; yazma
 * kararı oturum deposunundur (birden fazla okul oturumu tutulabilir, KK-801).
 */
export interface NormalGirisYaniti {
  token: string;
  parola_degisim_zorunlu?: false;
  personel: {
    id: string;
    ad: string;
    soyad: string;
    rol: number;
    rol_adi: string;
    noktalar: string[];
    tum_noktalar: boolean;
  };
  okul: { id: string; ad: string };
}

export interface ParolaDegisimGirisYaniti {
  parola_degisim_zorunlu: true;
  degisim_token: string;
  personel: { id: string; ad: string; soyad: string };
  okul: { id: string; ad: string };
}

export type GirisYaniti = NormalGirisYaniti | ParolaDegisimGirisYaniti;

export async function girisYap(
  okulKodu: string,
  eposta: string,
  parola: string,
): Promise<GirisYaniti> {
  const yanit = await fetch(adres("/kimlik/personel/giris"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      okul_kodu: okulKodu,
      eposta,
      parola,
    }),
  });
  if (!yanit.ok) return hatayiCoz(yanit);
  return govdeCoz<GirisYaniti>(yanit);
}

export async function zorunluParolaDegistir(
  degisimTokeni: string,
  yeniParola: string,
): Promise<NormalGirisYaniti> {
  const yanit = await fetch(adres("/kimlik/personel/parola-degistir"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      degisim_token: degisimTokeni,
      yeni_parola: yeniParola,
      yeni_parola_tekrar: yeniParola,
    }),
  });
  if (!yanit.ok) return hatayiCoz(yanit);
  return govdeCoz<NormalGirisYaniti>(yanit);
}

/**
 * KK-608 · yetki gerektiren dosya indirmesi.
 *
 * Düz bir <a href> Authorization başlığını taşıyamaz; dosya fetch ile alınıp
 * blob olarak indirilir. Sunucu satır limitini aşarsa X-Aktarim-Kirpildi
 * başlığını "1" döner ve dosyanın içine bir "UYARI" sayfası ekler — bu sonuç
 * çağırana DÖNDÜRÜLÜR ki ekran kullanıcıyı uyarabilsin. Sessiz kırpma, eksik
 * veriyle alınmış kararların kaynağıdır.
 */
export interface AktarimSonucu {
  kirpildi: boolean;
  limit: string | null;
  dosyaAdi: string;
}

export async function raporIndir(
  rapor: string,
  dosyaKoku: string,
  bicim: "xlsx" | "csv",
  sorgu?: SorguParam,
): Promise<AktarimSonucu> {
  const token = tokenOku();
  const yol =
    rapor === "z" ? "/rapor/z/disa-aktar" : `/rapor/${rapor}/disa-aktar`;
  const yanit = await fetch(adres(yol, { ...sorgu, bicim }), {
    method: "GET",
    headers: { ...basliklar(false, token), Accept: "*/*" },
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);

  const govde = await yanit.blob();
  const dosyaAdi = `${dosyaKoku}.${bicim}`;
  const url = URL.createObjectURL(govde);
  const bag = document.createElement("a");
  bag.href = url;
  bag.download = dosyaAdi;
  document.body.appendChild(bag);
  bag.click();
  bag.remove();
  URL.revokeObjectURL(url);

  return {
    kirpildi: yanit.headers.get("X-Aktarim-Kirpildi") === "1",
    limit: yanit.headers.get("X-Aktarim-Satir-Limiti"),
    dosyaAdi,
  };
}
